/**
 * Event Bus for communication between microfrontends
 * Provides a centralized way to emit and listen to events across the application
 */
class EventBus {
    constructor() {
        this.events = {};
        if (EventBus.instance) {
            return EventBus.instance;
        }
        EventBus.instance = this;
    }
    /**
     * Emit an event to all registered listeners
     * @param event - The event object containing type, payload, source, and timestamp
     */
    emit(event) {
        const { type } = event;
        if (this.events[type]) {
            this.events[type].forEach(callback => {
                try {
                    callback(event);
                }
                catch (error) {
                    console.error(`Error in event listener for ${type}:`, error);
                }
            });
        }
        // Also emit to wildcard listeners
        if (this.events['*']) {
            this.events['*'].forEach(callback => {
                try {
                    callback(event);
                }
                catch (error) {
                    console.error(`Error in wildcard event listener:`, error);
                }
            });
        }
    }
    /**
     * Register a listener for a specific event type
     * @param eventType - The type of event to listen for (or '*' for all events)
     * @param callback - The function to call when the event is emitted
     * @returns A function to unsubscribe the listener
     */
    on(eventType, callback) {
        if (!this.events[eventType]) {
            this.events[eventType] = [];
        }
        this.events[eventType].push(callback);
        // Return unsubscribe function
        return () => {
            this.off(eventType, callback);
        };
    }
    /**
     * Remove a specific listener for an event type
     * @param eventType - The type of event
     * @param callback - The callback function to remove
     */
    off(eventType, callback) {
        if (this.events[eventType]) {
            this.events[eventType] = this.events[eventType].filter(cb => cb !== callback);
        }
    }
    /**
     * Remove all listeners for a specific event type
     * @param eventType - The type of event to clear
     */
    removeAllListeners(eventType) {
        delete this.events[eventType];
    }
    /**
     * Remove all listeners for all events
     */
    clear() {
        this.events = {};
    }
    /**
     * Get the number of listeners for a specific event type
     * @param eventType - The type of event
     * @returns The number of listeners
     */
    listenerCount(eventType) {
        return this.events[eventType] ? this.events[eventType].length : 0;
    }
    /**
     * Get all registered event types
     * @returns Array of event types
     */
    eventNames() {
        return Object.keys(this.events);
    }
}
// Create and export singleton instance
export const eventBus = new EventBus();
// Helper functions for common event types
export const emitNavigationEvent = (path, params, source = 'unknown') => {
    eventBus.emit({
        type: 'NAVIGATION',
        payload: { path, params },
        source,
        timestamp: Date.now()
    });
};
export const emitUserEvent = (type, user, source = 'unknown') => {
    eventBus.emit({
        type,
        payload: { user },
        source,
        timestamp: Date.now()
    });
};
export const emitNotificationEvent = (message, type = 'info', duration, source = 'unknown') => {
    eventBus.emit({
        type: 'NOTIFICATION',
        payload: { message, type, duration },
        source,
        timestamp: Date.now()
    });
};
export const emitCustomEvent = (type, payload, source = 'unknown') => {
    eventBus.emit({
        type,
        payload,
        source,
        timestamp: Date.now()
    });
};
// React hook for using the event bus
export const useEventBus = () => {
    return {
        emit: eventBus.emit.bind(eventBus),
        on: eventBus.on.bind(eventBus),
        off: eventBus.off.bind(eventBus),
        emitNavigation: emitNavigationEvent,
        emitUser: emitUserEvent,
        emitNotification: emitNotificationEvent,
        emitCustom: emitCustomEvent
    };
};
export default eventBus;
